import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import {
  Activity, Cpu, MemoryStick, Database, Cloud, Zap, Upload, Users, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";

const REFRESH_MS = 12000;

const STATUS_COLORS = {
  healthy: { bg: "bg-[#10B981]", text: "text-[#10B981]", label: "Healthy", icon: CheckCircle2, border: "border-[#10B981]" },
  warning: { bg: "bg-[#F59E0B]", text: "text-[#F59E0B]", label: "Warning", icon: AlertTriangle, border: "border-[#F59E0B]" },
  critical: { bg: "bg-[#EF4444]", text: "text-[#EF4444]", label: "Critical", icon: XCircle, border: "border-[#EF4444]" },
};

function fmtUptime(sec) {
  if (!sec || sec < 0) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

function StatusPill({ status, size = "md", testId }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.critical;
  const Icon = c.icon;
  const padding = size === "sm" ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider border ${c.border} ${c.text} bg-white ${padding}`}
      data-testid={testId}
    >
      <Icon size={size === "sm" ? 10 : 12} />
      {c.label}
    </span>
  );
}

function Dot({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.critical;
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.bg}`} />;
}

function Card({ title, icon: Icon, status, children, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-6 flex flex-col gap-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-[#5B4CFF] shrink-0">
            <Icon size={16} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-mono tracking-widest text-[#475569]">{title.toUpperCase()}</p>
          </div>
        </div>
        {status && <StatusPill status={status} size="sm" testId={`${testId}-status`} />}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Bar({ value, warn = 70, crit = 90 }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  const color = pct >= crit ? "bg-[#EF4444]" : pct >= warn ? "bg-[#F59E0B]" : "bg-[#10B981]";
  return (
    <div className="w-full h-2 bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function AdminSystem() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const res = await api.get("/admin/system");
      setData(res.data);
      setError(null);
      setLastFetch(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnce();
    if (paused) return;
    timerRef.current = setInterval(fetchOnce, REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [paused]);

  const overall = data?.overall_status || "healthy";

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl" data-testid="system-monitor-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="kicker mb-2 inline-flex items-center gap-2"><Activity size={12} /> LIVE VITALS</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter">System Monitor</h1>
          <p className="mt-2 text-sm text-[#475569]">
            Auto-refreshes every {REFRESH_MS / 1000}s. Lightweight — no history, no logs.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {data && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 border ${STATUS_COLORS[overall].border} bg-white`} data-testid="overall-status">
              <Dot status={overall} />
              <span className="text-xs font-mono tracking-widest text-[#0F172A]">
                OVERALL · {STATUS_COLORS[overall].label.toUpperCase()}
              </span>
            </div>
          )}
          <button
            onClick={() => setPaused((p) => !p)}
            className="text-xs font-semibold inline-flex items-center gap-1.5 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-2 transition-colors"
            data-testid="pause-btn"
          >
            {paused ? "Resume auto-refresh" : "Pause auto-refresh"}
          </button>
          <button
            onClick={fetchOnce}
            className="text-xs font-semibold inline-flex items-center gap-1.5 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white px-3 py-2 transition-colors"
            data-testid="refresh-now-btn"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 border border-[#EF4444]/40 bg-[#fef2f2] p-4 text-xs text-[#0F172A] inline-flex items-center gap-2" data-testid="monitor-error">
          <XCircle size={14} className="text-[#EF4444]" /> Monitor error: {error}
        </div>
      )}

      {loading && !data ? (
        <p className="font-mono text-sm text-[#475569]">LOADING…</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* API */}
            <Card title="API server" icon={Zap} status={data.api.status} testId="card-api">
              <p className="font-display text-2xl font-bold text-[#0F172A]">
                {data.api.response_ms} <span className="text-sm text-[#475569] font-normal">ms</span>
              </p>
              <p className="text-xs text-[#475569] mt-1">Round-trip on this check</p>
              <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between text-xs text-[#475569]">
                <span>Uptime</span>
                <span className="font-mono text-[#0F172A]">{fmtUptime(data.uptime_seconds)}</span>
              </div>
            </Card>

            {/* Database */}
            <Card title="MongoDB" icon={Database} status={data.database.status} testId="card-db">
              <p className="font-display text-2xl font-bold text-[#0F172A]">
                {data.database.response_ms ?? "—"} <span className="text-sm text-[#475569] font-normal">ms</span>
              </p>
              <p className="text-xs text-[#475569] mt-1">Ping response</p>
              <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between text-xs text-[#475569]">
                <span>Connection</span>
                <span className={`font-mono ${data.database.ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {data.database.ok ? "OK" : "DOWN"}
                </span>
              </div>
            </Card>

            {/* R2 */}
            <Card title="Cloudflare R2" icon={Cloud} status={data.storage.status} testId="card-storage">
              <p className="font-display text-2xl font-bold text-[#0F172A]">
                {data.storage.response_ms ?? "—"} <span className="text-sm text-[#475569] font-normal">ms</span>
              </p>
              <p className="text-xs text-[#475569] mt-1">Bucket <span className="font-mono">{data.storage.bucket}</span></p>
              <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between text-xs text-[#475569]">
                <span>Read / Write</span>
                <span className={`font-mono ${data.storage.ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {data.storage.ok ? "OK" : "DOWN"}
                </span>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Active Requests */}
            <Card title="Active requests" icon={Users} testId="card-requests">
              <p className="font-display text-4xl font-extrabold text-[#0F172A]" data-testid="active-requests-value">
                {data.activity.active_requests}
              </p>
              <p className="text-xs text-[#475569] mt-1">In flight right now</p>
            </Card>

            {/* Active Uploads */}
            <Card title="Photo uploads" icon={Upload} testId="card-uploads">
              <p className="font-display text-4xl font-extrabold text-[#0F172A]" data-testid="active-uploads-value">
                {data.activity.active_uploads}
              </p>
              <p className="text-xs text-[#475569] mt-1">Processing (OCR + R2)</p>
            </Card>

            {/* CPU */}
            <Card title="CPU usage" icon={Cpu} status={data.resources.cpu_status} testId="card-cpu">
              <p className="font-display text-4xl font-extrabold text-[#0F172A]">
                {data.resources.cpu_percent}<span className="text-xl">%</span>
              </p>
              <div className="mt-3"><Bar value={data.resources.cpu_percent} warn={70} crit={90} /></div>
            </Card>

            {/* Memory */}
            <Card title="Memory usage" icon={MemoryStick} status={data.resources.memory_status} testId="card-memory">
              <p className="font-display text-4xl font-extrabold text-[#0F172A]">
                {data.resources.memory_percent}<span className="text-xl">%</span>
              </p>
              <p className="text-xs text-[#475569] mt-1 font-mono">
                {data.resources.memory_used_mb} / {data.resources.memory_total_mb} MB
              </p>
              <div className="mt-2"><Bar value={data.resources.memory_percent} warn={75} crit={90} /></div>
            </Card>
          </div>

          <p className="text-xs text-[#475569] font-mono" data-testid="last-updated">
            LAST UPDATED · {lastFetch ? lastFetch.toLocaleTimeString() : "—"}
            {paused && " · AUTO-REFRESH PAUSED"}
          </p>
        </>
      ) : null}
    </div>
  );
}
